import gql from "graphql-tag"
import { ResponsiveImageFields } from "../../dato/DatoContent/fragments"

/**
 * Per-car shell + steps for vehicle select.
 *
 * When `NEXT_PUBLIC_UPHOLSTERY_STEPS_V2=true`, Build / Kit / Pattern / Design /
 * Material nested under `steps` are discarded in
 * `loadUpholsteryVehicleSelection` and replaced from `allUpholsteryStepsV2Query`.
 * Dato modular content cannot filter those blocks out of this query by key, so
 * the app strips them client-side instead of using them as a fallback.
 */
export const upholsteryStepsQuery = gql`
  ${ResponsiveImageFields}
  query upholsteryWithSteps($make: String!, $model: String!, $years: String!) {
    upholsteryCar(
      filter: {
        make: { eq: $make }
        model: { eq: $model }
        years: { matches: { pattern: $years } }
      }
    ) {
      id
      make
      years
      model
      sku
      options
      image {
        responsiveImage {
          ...ResponsiveImageFields
        }
      }
      steps {
        id
        key
        title
        factoryMatches {
          id
          keyPairs {
            id
            key
            values
          }
          factoryMatchOption {
            id
            title
            sku
            value
            image {
              responsiveImage {
                ...ResponsiveImageFields
              }
            }
          }
          image {
            responsiveImage {
              ...ResponsiveImageFields
            }
          }
          sku
        }
        compatibility {
          id
          key
          values
        }
        category {
          optionName
          optionValues {
            id
            optionName
            sku
            optionValue
            optionImage {
              responsiveImage {
                src
              }
            }
            compatibility {
              key
              values
            }
            twoRowPrice
            threeRowPrice
          }
        }
        substeps {
          id
          key
          title
          factoryMatches {
            id
            keyPairs {
              id
              key
              values
            }
            image {
              responsiveImage {
                ...ResponsiveImageFields
              }
            }
            sku
          }
          colorInsertMatches {
            id
            image {
              responsiveImage {
                ...ResponsiveImageFields
              }
            }
            keyPairs {
              key
              values
            }
          }
          category {
            optionName
            optionValues {
              id
              sku
              optionName
              optionValue
              optionImage {
                responsiveImage {
                  src
                }
              }
              compatibility {
                key
                values
              }
              twoRowPrice
              threeRowPrice
              slug
            }
          }
          substeps {
            id
            key
            title
            category {
              optionName
              optionValues {
                id
                sku
                optionName
                optionValue
                optionImage {
                  responsiveImage {
                    src
                  }
                }
                twoRowPrice
                threeRowPrice
              }
            }
          }
          defaultValue
        }
      }
    }
  }
`

export const allUpholsteryCarsQuery = gql`
  ${ResponsiveImageFields}
  query allUpholsteryCars {
    allUpholsteryCars(first: 250) {
      id
      make
      model
      years
      image {
        responsiveImage {
          ...ResponsiveImageFields
        }
      }
    }
  }
`

/**
 * Global style steps from Dato model **Upholstery Steps V2**
 * (GraphQL: `allUpholsterCarsSteps` / API key `upholster_cars_step`).
 *
 * Style-only: Build, Kit, Pattern, Design, Material.
 * Filter by key so brand Shopify maps (`honda-shopify-variants`, …) on the
 * same model cannot crowd out the five required style steps (`first` alone
 * is not enough once those brand records exist).
 */
export const allUpholsteryStepsV2Query = gql`
  query allUpholsteryStepsV2 {
    allUpholsterCarsSteps(
      filter: {
        OR: [
          { key: { eq: "build" } }
          { key: { eq: "kit" } }
          { key: { eq: "pattern" } }
          { key: { eq: "design" } }
          { key: { eq: "material" } }
        ]
      }
      first: 10
    ) {
      id
      key
      name
      description
      category {
        optionName
        optionValues {
          id
          optionName
          sku
          optionValue
          slug
          optionImage {
            responsiveImage {
              src
            }
          }
          compatibility {
            key
            values
          }
          twoRowPrice
          threeRowPrice
        }
      }
      steps {
        id
        key
        name
        value
        buildOptions
        category {
          optionName
          optionValues {
            id
            optionName
            sku
            optionValue
            slug
            optionImage {
              responsiveImage {
                src
              }
            }
            compatibility {
              key
              values
            }
            twoRowPrice
            threeRowPrice
          }
        }
        substeps {
          id
          key
          name
          value
          category {
            optionName
            optionValues {
              id
              optionName
              sku
              optionValue
              optionImage {
                responsiveImage {
                  src
                }
              }
              twoRowPrice
              threeRowPrice
            }
          }
        }
      }
    }
  }
`

/**
 * Load ONE brand's Shopify variant lookup from Dato.
 *
 * Example: key = "honda-shopify-variants"
 * Returns the record's `options` JSON (the shopifyVariants list lives there).
 *
 * Used by fetchShopifyVariantsByMake — not by the style-steps query.
 */
export const upholsteryShopifyVariantsByMakeQuery = gql`
  query upholsteryShopifyVariantsByMake($key: String!) {
    allUpholsterCarsSteps(filter: { key: { eq: $key } }, first: 1) {
      id
      key
      name
      options
    }
  }
`
